import type { CampaignStatus } from './campaigns.types';
import type { CampaignAudienceSelection } from '../utils/campaign-audience.utils';
import type { CampaignScheduleDraft } from '../utils/campaign-schedule-draft.utils';

export type CampaignRestAudience = {
  listIds: string[];
  segmentIds: string[];
  excludeListIds: string[];
  excludeSegmentIds: string[];
  estimatedRecipients: number | null;
  sendableCount: number | null;
  estimatedAt: string | null;
};

export type CampaignRestDetail = {
  id: string;
  name: string;
  subject: string;
  /** UI label "preheader"; wire field is previewText. */
  preheader: string;
  channel: string;
  status: CampaignStatus;
  fromName: string;
  fromEmail: string;
  fromAddress: string;
  replyTo: string;
  templateIds: string[];
  contentIds: string[];
  tags: string[];
  audience: CampaignRestAudience;
  audienceSize: number | null;
  sendableCount: number | null;
  contentHasHtml: boolean;
  scheduledAt: string | null;
  timeZone: string | null;
  isRecurring: boolean;
  /** The schedule saved on the draft, restored when the wizard reopens. */
  draftSchedule: CampaignScheduleDraft | null;
  createdAt: string | null;
  updatedAt: string | null;
};

export type CampaignListStats = {
  recipients: number;
  delivered: number;
  /** Fraction 0..1; `null` when nothing was delivered. */
  openRate: number | null;
  clickRate: number | null;
};

export type CampaignRestSummary = {
  id: string;
  name: string;
  subject: string;
  status: string;
  channel: string;
  audienceSize: number | null;
  sendableCount: number | null;
  estimatedAt: string | null;
  scheduledAt: string | null;
  sentAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  templateIds: string[];
  /** `null` until the campaign has sent — drafts never have stats. */
  stats: CampaignListStats | null;
};

export type CampaignRestListResult = {
  items: CampaignRestSummary[];
  totalCount: number;
  pageNo: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
};

export type CreateCampaignRestInput = {
  name: string;
  channel?: string;
  subject?: string;
  /** UI preheader — mapped to wire previewText. */
  preheader?: string;
  fromName?: string;
  fromEmail?: string;
  fromAddress?: string;
  replyTo?: string;
  templateId?: string;
};

export type UpdateCampaignRestInput = {
  name?: string;
  subject?: string;
  preheader?: string;
  fromName?: string;
  fromEmail?: string;
  fromAddress?: string;
  replyTo?: string;
  /** A plan saves it, `null` clears it, `undefined` leaves the saved one alone. */
  draftSchedule?: CampaignScheduleDraft | null;
};

export type SetCampaignAudienceInput = {
  listIds: string[];
  segmentIds: string[];
  excludeListIds?: string[];
  excludeSegmentIds?: string[];
};

export type DuplicateCampaignRestInput = {
  name: string;
};

export type SendCampaignRestInput = {
  campaignId: string;
  /** Fresh UUID per user-initiated send attempt (guide §11.1). */
  idempotencyKey: string;
  /** Last sendable count shown to the user (guide §11). */
  confirmEstimatedRecipients?: number;
};

export type ScheduleRecurrenceRestInput = {
  frequency: 'daily' | 'weekly' | 'monthly';
  interval?: number | null;
  byWeekday?: string[] | null;
  byMonthDay?: number[] | null;
  startDate?: string | null;
  endDate?: string | null;
  occurrences?: number | null;
  timeOfDay?: string | null;
  timeZone?: string | null;
};

/** Guide §5 — POST /v1/campaigns/{id}/schedule body + Idempotency-Key. */
export type ScheduleCampaignRestInput = {
  campaignId: string;
  idempotencyKey: string;
  mode: 'scheduled' | 'recurring';
  scheduledAt?: string | null;
  timeZone?: string | null;
  throttlePerHour?: number | null;
  recurrence?: ScheduleRecurrenceRestInput | null;
};

export type CancelScheduleResult = {
  campaignId: string;
  cancelledJobIds: string[];
  campaignStatus: string;
};

export type PreSendCheck = {
  code: string;
  message: string;
  severity: 'error' | 'warning' | string;
};

export type AudienceSkipCounts = {
  unsubscribed: number;
  pending: number;
  suppressed: number;
  noAddress: number;
  invalidEmail: number;
  noSubscriptionStatus: number;
};

export type SendEligibility = {
  sendable: boolean;
  blocked: PreSendCheck[];
  /** @deprecated Prefer `blocked` — kept for older call sites. */
  blockedMessages: string[];
  recipients: {
    include: number;
    duplicatesMerged: number;
    exclude: number;
    removedByExclusion: number;
    net: number;
    sendable: number;
    skips: AudienceSkipCounts;
  } | null;
};

export type AudienceEstimate = {
  includeCount: number;
  duplicatesMerged: number;
  excludeCount: number;
  removedByExclusion: number;
  netCount: number;
  sendableCount: number;
  skips: AudienceSkipCounts;
  truncated: boolean;
  evaluatedAt: string | null;
};

export type SendJobResult = {
  jobId: string;
  campaignId: string;
  mode: string;
  status: string;
  recipientCount: number;
  scheduledAt: string | null;
};

export type CampaignContentSanitization = {
  removed: string[];
  count: number;
};

export type CampaignContentVersion = {
  contentId: string;
  version: number;
  html: string;
  plainText: string | null;
  links: string[];
  isCurrent: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
  sanitization: CampaignContentSanitization;
};

export type PutCampaignContentInput = {
  html: string;
  /** `null` → server derives plain text (guide §4.1). */
  plainText?: string | null;
};

export type ApplyTemplateInput = {
  templateId: string;
};

export type TestSendInput = {
  campaignId: string;
  recipients: string[];
  idempotencyKey: string;
  contactId?: string;
};

export type TestSendResult = {
  accepted: number;
  recipients: string[];
};

export type SendJobSkipReason = {
  reason: string;
  count: number;
};

export type SendJobProgress = {
  status: string;
  recipientCount: number;
  processedCount: number;
  sentCount: number;
  failedCount: number;
  skippedCount: number;
  skipReasons: SendJobSkipReason[];
  /** `null` / unknown when job just started — do not treat 0 as real throughput. */
  throughputPerMinute: number | null;
  startedAt: string | null;
  estimatedCompletionAt: string | null;
  lastError: string | null;
};

export type GetCampaignsRestParams = {
  pageNo?: number;
  pageSize?: number;
  q?: string;
  /** API status values: draft|scheduled|sending|sent|failed|cancelled */
  status?: string[];
  /** API sort: created|updated|name|scheduled */
  sort?: string;
  order?: 'asc' | 'desc';
  channel?: 'email' | 'sms';
};

/** Map wizard audience selection → REST PUT body. */
export const audienceSelectionToRestInput = (
  selection: CampaignAudienceSelection
): SetCampaignAudienceInput => ({
  listIds: [...selection.includeListIds],
  segmentIds: [...selection.includeSegmentIds],
  excludeListIds: [...(selection.excludeListIds ?? [])],
  excludeSegmentIds: [...selection.excludeSegmentIds],
});
